import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Wand2, Bot, BarChart3, MessageSquare, Users,
  Plus, Trash2, PanelLeftClose, PanelLeft,
  Pencil, Check, X, Loader2, MessageCircle,
  PanelRightClose, PanelRight,
} from 'lucide-react';
import { cn } from '@/utils/cn';
import { useCoworkStore } from '@/store/coworkStore';
import { ChatMessageComponent } from '@/pages/cowork/ChatMessage';
import { ChatInput } from '@/pages/cowork/ChatInput';
import { ActionPanel } from '@/pages/cowork/ActionPanel';
import type { ConversationSummary } from '@/services/cowork.service';

// ── Suggestions for empty state ──
const SUGGESTIONS = [
  { icon: Bot, label: 'List all my agents', color: 'bg-violet-100 text-violet-600' },
  { icon: BarChart3, label: 'Show dashboard stats', color: 'bg-blue-100 text-blue-600' },
  { icon: MessageSquare, label: 'How many active conversations?', color: 'bg-emerald-100 text-emerald-600' },
  { icon: Users, label: 'Show my team members', color: 'bg-amber-100 text-amber-600' },
];

// ── Date grouping helpers ──
function isToday(date: Date): boolean {
  const now = new Date();
  return date.toDateString() === now.toDateString();
}

function isYesterday(date: Date): boolean {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return date.toDateString() === yesterday.toDateString();
}

function isThisWeek(date: Date): boolean {
  const now = new Date();
  const weekAgo = new Date();
  weekAgo.setDate(now.getDate() - 7);
  return date >= weekAgo && !isToday(date) && !isYesterday(date);
}

type DateGroup = 'Today' | 'Yesterday' | 'This Week' | 'Older';

function getDateGroup(dateStr: string): DateGroup {
  const date = new Date(dateStr);
  if (isToday(date)) return 'Today';
  if (isYesterday(date)) return 'Yesterday';
  if (isThisWeek(date)) return 'This Week';
  return 'Older';
}

function groupConversations(conversations: ConversationSummary[]): Map<DateGroup, ConversationSummary[]> {
  const groups = new Map<DateGroup, ConversationSummary[]>();
  const order: DateGroup[] = ['Today', 'Yesterday', 'This Week', 'Older'];
  for (const g of order) groups.set(g, []);

  for (const conv of conversations) {
    const group = getDateGroup(conv.updated_at);
    groups.get(group)!.push(conv);
  }

  // Remove empty groups
  for (const g of order) {
    if (groups.get(g)!.length === 0) groups.delete(g);
  }
  return groups;
}

function getInitials(name: string | null): string {
  if (!name) return '?';
  return name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);
}

// ── Sidebar Conversation Item ──
interface ConversationItemProps {
  conversation: ConversationSummary;
  isActive: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onRename: (title: string) => void;
}

const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  onSelect,
  onDelete,
  onRename,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [showActions, setShowActions] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTitle(conversation.title || '');
    setIsEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const confirmRename = () => {
    const trimmed = editTitle.trim();
    if (trimmed && trimmed !== conversation.title) {
      onRename(trimmed);
    }
    setIsEditing(false);
  };

  const cancelRename = () => setIsEditing(false);

  return (
    <div
      onClick={isEditing ? undefined : onSelect}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
      className={cn(
        'group relative flex items-center gap-2 px-3 py-2.5 cursor-pointer rounded-lg mx-1.5 transition-all duration-100',
        isActive
          ? 'bg-violet-100 border-l-[3px] border-violet-500'
          : 'hover:bg-violet-50 border-l-[3px] border-transparent'
      )}
    >
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') confirmRename();
                if (e.key === 'Escape') cancelRename();
              }}
              className="w-full text-sm bg-white px-1.5 py-0.5 rounded border border-violet-300 focus:outline-none focus:ring-1 focus:ring-violet-400"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); confirmRename(); }}
              className="p-0.5 text-emerald-600 hover:bg-emerald-50 rounded"
            >
              <Check className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); cancelRename(); }}
              className="p-0.5 text-gray-400 hover:bg-gray-100 rounded"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ) : (
          <>
            <p className={cn(
              'text-sm truncate leading-tight',
              isActive ? 'font-semibold text-violet-900' : 'text-gray-700'
            )}>
              {conversation.title || 'New conversation'}
            </p>
            {conversation.created_by_name && (
              <div className="flex items-center gap-1.5 mt-0.5">
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-200 text-[8px] font-bold text-gray-500 shrink-0">
                  {getInitials(conversation.created_by_name)}
                </span>
                <span className="text-[10px] text-gray-400">{conversation.created_by_name}</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Hover actions */}
      {showActions && !isEditing && (
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            onClick={startEditing}
            className="p-1 rounded hover:bg-violet-200 text-gray-400 hover:text-gray-600 transition-colors"
            title="Rename"
          >
            <Pencil className="w-3 h-3" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="p-1 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      )}
    </div>
  );
};

// ── Sidebar ──
interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const {
    conversations,
    activeConversationId,
    isLoadingConversations,
    createNewConversation,
    selectConversation,
    deleteConversation,
    renameConversation,
  } = useCoworkStore();

  const grouped = groupConversations(conversations);

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          'flex flex-col bg-white border-r-2 border-[#1a1a2e] h-full z-40',
          'w-[280px] shrink-0',
          // Mobile: absolute overlay
          'fixed lg:relative',
          'transition-transform duration-200 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
          // On lg+ always visible
          'lg:translate-x-0'
        )}
      >
        {/* Sidebar header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-bold text-gray-900 font-heading tracking-tight">Co-work</h2>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => { createNewConversation(); navigate('/cowork'); onClose(); }}
              className={cn(
                'flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-semibold rounded-lg',
                'bg-violet-500 text-white',
                'border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e]',
                'hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none',
                'transition-all duration-150'
              )}
            >
              <Plus className="w-3.5 h-3.5" />
              New
            </button>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors lg:hidden"
            >
              <PanelLeftClose className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Conversation list */}
        <div className="flex-1 overflow-y-auto py-2">
          {isLoadingConversations && conversations.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
            </div>
          ) : conversations.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
              <MessageCircle className="w-8 h-8 text-gray-300 mb-2" />
              <p className="text-xs text-gray-400">No conversations yet</p>
              <p className="text-[10px] text-gray-300 mt-0.5">Start a new chat to begin</p>
            </div>
          ) : (
            Array.from(grouped.entries()).map(([group, convs]) => (
              <div key={group} className="mb-1">
                <p className="px-4 py-1.5 text-[10px] uppercase text-gray-400 font-semibold tracking-wider">
                  {group}
                </p>
                {convs.map((conv) => (
                  <ConversationItem
                    key={conv.id}
                    conversation={conv}
                    isActive={conv.id === activeConversationId}
                    onSelect={() => { selectConversation(conv.id); navigate(`/cowork/${conv.id}`); onClose(); }}
                    onDelete={() => deleteConversation(conv.id)}
                    onRename={(title) => renameConversation(conv.id, title)}
                  />
                ))}
              </div>
            ))
          )}
        </div>
      </aside>
    </>
  );
};

// ── Main Chat Area ──
const ChatArea: React.FC<{ onOpenSidebar: () => void }> = ({ onOpenSidebar }) => {
  const {
    messages,
    isLoading,
    isStreaming,
    activeConversationId,
    conversations,
    sendMessage,
    mode,
    setMode,
    panel,
    openPanel,
    closePanel,
  } = useCoworkStore();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading, isStreaming]);

  const handleQuickReply = (text: string) => {
    sendMessage(text);
  };

  const activeConv = conversations.find((c) => c.id === activeConversationId) as any;

  return (
    <div className="flex flex-col flex-1 min-w-0 h-full">
      {/* Chat header */}
      <div className="flex items-center gap-3 px-4 py-2.5 border-b border-gray-200 bg-white/80 backdrop-blur-sm shrink-0">
        <button
          onClick={onOpenSidebar}
          className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors lg:hidden"
        >
          <PanelLeft className="w-4 h-4" />
        </button>
        <div className="flex-1 min-w-0">
          {activeConv ? (
            <>
              <p className="text-sm font-semibold text-gray-900 font-heading truncate">
                {activeConv.title || 'New conversation'}
              </p>
              {activeConv.created_by_name && (
                <p className="text-[10px] text-gray-400">by {activeConv.created_by_name}</p>
              )}
            </>
          ) : (
            <p className="text-sm font-semibold text-gray-900 font-heading">New conversation</p>
          )}
        </div>
        {/* Mode toggle */}
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setMode(mode === 'standard' ? 'advanced' : 'standard')}
            className={cn(
              'flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-all border',
              mode === 'advanced'
                ? 'bg-violet-100 text-violet-700 border-violet-300'
                : 'bg-blue-50 text-blue-600 border-blue-200'
            )}
          >
            <span className={cn('w-1.5 h-1.5 rounded-full', mode === 'advanced' ? 'bg-violet-500' : 'bg-blue-500')} />
            {mode === 'advanced' ? 'Advanced' : 'Standard'}
          </button>

          {/* Total cost badge */}
          {activeConv?.total_cost_usd > 0 && (
            <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-gray-50 border border-gray-200">
              <span className="text-[10px] text-gray-400">{((activeConv.total_input_tokens || 0) + (activeConv.total_output_tokens || 0)).toLocaleString()} tokens</span>
              <span className="text-[10px] text-gray-300">·</span>
              <span className="text-[10px] font-semibold text-gray-600">
                ${activeConv.total_cost_usd < 0.01 ? Number(activeConv.total_cost_usd).toFixed(4) : Number(activeConv.total_cost_usd).toFixed(2)}
              </span>
            </div>
          )}

          {/* Action panel toggle */}
          {panel.history.length > 0 && (
            <button
              onClick={() => {
                if (panel.isOpen) {
                  closePanel()
                } else {
                  openPanel(panel.entityType!, panel.entityData, panel.toolName!)
                  useCoworkStore.getState().refreshPanel()
                }
              }}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                panel.isOpen
                  ? 'text-violet-600 bg-violet-100 hover:bg-violet-200'
                  : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
              )}
              title={panel.isOpen ? 'Close panel' : 'Open panel'}
            >
              {panel.isOpen ? <PanelRightClose className="w-4 h-4" /> : <PanelRight className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Messages area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 py-6">
          {/* Empty state */}
          {messages.length === 0 && !isLoading && (
            <div className="flex flex-col items-center justify-center py-20">
              <div className="w-16 h-16 bg-violet-100 rounded-2xl border-2 border-[#1a1a2e] shadow-[3px_3px_0px_#1a1a2e] flex items-center justify-center mb-5">
                <Wand2 className="w-8 h-8 text-violet-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 font-heading mb-1">Co-work Assistant</h2>
              <p className="text-sm text-gray-500 mb-8 text-center max-w-md">
                Your AI co-worker for managing agents, analyzing conversations, reviewing configs, and more.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg">
                {SUGGESTIONS.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => sendMessage(s.label)}
                    className={cn(
                      'flex items-center gap-3 px-4 py-3 rounded-xl text-left',
                      'border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e] bg-white',
                      'hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none',
                      'transition-all duration-150'
                    )}
                  >
                    <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center shrink-0', s.color)}>
                      <s.icon className="w-4 h-4" />
                    </div>
                    <span className="text-sm text-gray-700">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          {messages.map((msg, idx) => (
            <ChatMessageComponent
              key={msg.id}
              message={msg}
              isLast={idx === messages.length - 1}
              onQuickReply={handleQuickReply}
            />
          ))}

          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input area */}
      <div className="border-t border-gray-200 bg-white/80 backdrop-blur-sm shrink-0">
        <div className="max-w-3xl mx-auto px-4 py-3">
          <ChatInput onSend={(msg) => sendMessage(msg)} isLoading={isLoading} />
        </div>
      </div>
    </div>
  );
};

// ── Page Root ──
export const CoWorkPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const { loadConversations, selectConversation, activeConversationId } = useCoworkStore();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Sync URL param → store: if URL has a conversationId, select it
  useEffect(() => {
    if (conversationId && conversationId !== activeConversationId) {
      selectConversation(conversationId);
    }
  }, [conversationId, activeConversationId, selectConversation]);

  return (
    <div className="flex -mx-4 sm:-mx-6 lg:-mx-8 -my-6 overflow-hidden" style={{ height: 'calc(100vh - 3.5rem)' }}>
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <ChatArea onOpenSidebar={() => setSidebarOpen(true)} />
      <ActionPanel />
    </div>
  );
};

export default CoWorkPage;
